import { useState, useEffect } from "react";
import { RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { IconPicker } from "@/components/appearance/IconPicker";
import { ColorSwatchPicker } from "@/components/appearance/ColorSwatchPicker";
import { AppIcon } from "@/lib/appearanceIcons";
import { chipStyle } from "@/lib/appearancePalette";
import { cn } from "@/lib/utils";

// Generic appearance editor for Category / Board / Stage. Draft-only until Save
// (never mutates the live entity while experimenting). Shows a Light + Dark
// preview of the entity's real chip/header treatment.
export function AppearanceDialog({ open, onOpenChange, title, label, initial, kind = "chip", onSave, canEdit = true }) {
  const [icon, setIcon] = useState(initial?.icon || "");
  const [colour, setColour] = useState(initial?.colour || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setIcon(initial?.icon || ""); setColour(initial?.colour || ""); } /* eslint-disable-next-line */ }, [open]);

  const save = async () => {
    setBusy(true);
    try { await onSave({ icon, colour }); onOpenChange(false); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="appearance-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{title}</DialogTitle>
          <DialogDescription>Set an optional icon and colour. Presentation only — it never changes task status.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Live preview — Light & Dark */}
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Preview</div>
            <div className="grid grid-cols-2 gap-2">
              <PreviewTile theme="light" label={label} icon={icon} colour={colour} kind={kind} />
              <PreviewTile theme="dark" label={label} icon={icon} colour={colour} kind={kind} />
            </div>
          </div>

          {!canEdit ? (
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="appearance-readonly">
              You don't have permission to edit this organisation appearance. Ask an owner or admin.
            </p>
          ) : (
            <>
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Colour</div>
                <ColorSwatchPicker value={colour} onChange={setColour} />
              </div>
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Icon</div>
                <IconPicker value={icon} onChange={setIcon} />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <Button variant="ghost" size="sm" disabled={!canEdit} onClick={() => { setIcon(""); setColour(""); }} data-testid="appearance-reset" className="gap-1.5 text-muted-foreground">
            <RotateCcw className="h-3.5 w-3.5" /> Reset to Nirvana Default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="appearance-cancel">Cancel</Button>
            {canEdit && <Button onClick={save} disabled={busy} data-testid="appearance-save">Save</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewTile({ theme, label, icon, colour, kind }) {
  const dark = theme === "dark";
  return (
    <div className={cn("rounded-xl border p-4", dark ? "dark border-white/10 bg-[#0b0f14]" : "border-slate-200 bg-white")}>
      <div className={cn("mb-2 text-[10px] font-semibold uppercase tracking-wider", dark ? "text-white/40" : "text-slate-400")}>{dark ? "Dark" : "Light"}</div>
      {kind === "header" ? (
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colour || (dark ? "#94a3b8" : "#64748b") }}>
          <AppIcon name={icon} className="h-3.5 w-3.5" />{label}
        </div>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[12px] font-medium"
          style={chipStyle(colour) || { color: dark ? "#cbd5e1" : "#475569", borderColor: dark ? "#ffffff20" : "#e2e8f0", backgroundColor: dark ? "#ffffff0d" : "#f1f5f9" }}>
          <AppIcon name={icon} className="h-3 w-3" />{label}
        </span>
      )}
    </div>
  );
}
